import moment from 'moment';

import { evaluateCondition } from './config/evaluateCondition';
import { isConfigEnabled } from './config/isConfigEnabled';
import { getTierControlField } from './tierRules';

export const APPROVAL_DATE_FIELD = 'approvalDate';
export const NEGOTIATION_STATUS_FIELD = 'negotiationStatus';

export const DEAL_REVIEW_STATUS = 'Deal Review';
export const NEW_NEGOTIATION_STATUS = 'New Negotiation';
export const NEGOTIATION_IN_PROGRESS_STATUS = 'Negotiation In Progress';
export const NEGOTIATION_COMPLETED_STATUS = 'Negotiation Completed';
export const NEGOTIATION_CANCELLED_STATUS = 'Negotiation Cancelled';

/** @deprecated Use NEGOTIATION_COMPLETED_STATUS */
export const NEGOTIATION_COMPLETE_STATUS = NEGOTIATION_COMPLETED_STATUS;

export const TIER_APPROVAL_DATE_FIELDS = {
    0: 'tier0ApprovalDate',
    1: 'tier1ApprovalDate',
    2: 'tier2ApprovalDate',
};

export const NEGOTIATION_STATUS_DEPENDENCY_FIELDS = [
    APPROVAL_DATE_FIELD,
    NEGOTIATION_STATUS_FIELD,
    ...Object.values(TIER_APPROVAL_DATE_FIELDS),
];

/**
 * Returns negotiation status dropdown options from behavior config.
 */
export const getNegotiationStatusOptions = (behaviorConfig) => {
    const options = behaviorConfig?.negotiationStatus?.options || [
        DEAL_REVIEW_STATUS,
        NEW_NEGOTIATION_STATUS,
        NEGOTIATION_IN_PROGRESS_STATUS,
        NEGOTIATION_COMPLETED_STATUS,
        NEGOTIATION_CANCELLED_STATUS,
    ];

    return options.map((status) => ({
        label: status,
        value: status,
    }));
};

/**
 * Default negotiation status when no relevant approval dates exist.
 */
export const getDefaultNegotiationStatus = (behaviorConfig) => {
    const rules = behaviorConfig?.negotiationStatus?.autoUpdate?.rules || [];
    const defaultRule = rules.find((rule) => rule.condition?.operator === 'default');

    return defaultRule?.set || DEAL_REVIEW_STATUS;
};

/**
 * Returns true when a date field holds a valid non-empty value.
 */
export const hasApprovalDate = (value) => {
    if (value === null || value === undefined || value === '') {
        return false;
    }

    return moment.parseZone(value).isValid();
};

/** @deprecated Use hasApprovalDate */
export const isApprovalDateFilled = hasApprovalDate;

/**
 * Maps topTierRequired to the corresponding tier approval date field key.
 */
export const getTopTierApprovalDateKey = (topTierRequired, behaviorConfig) => {
    const topTierVal = topTierRequired;
    const tierMatch = topTierVal == null ? null : String(topTierVal).match(/tier\s*(\d+)/i);
    const tierNumber = tierMatch ? parseInt(tierMatch[1], 10) : null;
    const tierApprovalDateFields = behaviorConfig?.negotiationStatus?.autoUpdate?.tierApprovalDateFields
        || TIER_APPROVAL_DATE_FIELDS;

    if (tierNumber == null) {
        return null;
    }

    return tierApprovalDateFields[tierNumber] || null;
};

/**
 * Returns true when the row's relevant top-tier approval date has a value.
 */
export const hasRelevantTopTierApprovalDate = (row = {}, behaviorConfig) => {
    const topTierField = getTierControlField(behaviorConfig);
    const approvalDateKey = getTopTierApprovalDateKey(row[topTierField], behaviorConfig);

    if (!approvalDateKey) {
        return false;
    }

    return hasApprovalDate(row[approvalDateKey]);
};

/**
 * Derives negotiationStatus from configured priority rules.
 */
export const deriveNegotiationStatus = (row = {}, behaviorConfig) => {
    const autoUpdate = behaviorConfig?.negotiationStatus?.autoUpdate;
    const context = {
        topTierField: behaviorConfig?.negotiationStatus?.topTierField || 'topTierRequired',
        tierApprovalDateFields: autoUpdate?.tierApprovalDateFields || TIER_APPROVAL_DATE_FIELDS,
    };

    const enabledRules = (autoUpdate?.rules || [])
        .filter((rule) => isConfigEnabled(rule))
        .sort((left, right) => (left.priority || 99) - (right.priority || 99));

    for (const rule of enabledRules) {
        if (evaluateCondition(row, rule.condition, context)) {
            return rule.set;
        }
    }

    return getDefaultNegotiationStatus(behaviorConfig);
};

/**
 * Returns true when saving this field can affect negotiationStatus.
 */
const isNegotiationStatusTriggerField = (fieldKey, row, behaviorConfig) => {
    const autoUpdate = behaviorConfig?.negotiationStatus?.autoUpdate;
    const topTierField = behaviorConfig?.negotiationStatus?.topTierField || 'topTierRequired';
    const approvalDateField = behaviorConfig?.negotiationStatus?.approvalDateField || 'approvalDate';

    if ((autoUpdate?.triggerFields?.always || []).includes(fieldKey)) {
        return true;
    }

    if (!autoUpdate?.triggerFields?.includeRelevantTierApprovalDate) {
        return false;
    }

    const approvalDateKey = getTopTierApprovalDateKey(row[topTierField], behaviorConfig);
    return approvalDateKey != null && fieldKey === approvalDateKey;
};

/**
 * Returns true when automatic negotiationStatus updates should be skipped.
 */
const shouldSkipNegotiationStatusAutoUpdate = (row, fieldKey, behaviorConfig) => {
    const statusField = behaviorConfig?.negotiationStatus?.field || NEGOTIATION_STATUS_FIELD;
    const autoUpdate = behaviorConfig?.negotiationStatus?.autoUpdate;

    if (fieldKey === autoUpdate?.skipWhenSavingField) {
        return true;
    }

    const currentStatus = row?.[statusField];
    return (autoUpdate?.skipWhenCurrentValueIn || []).includes(currentStatus);
};

/**
 * Collects dependency field keys for table API requests.
 */
export const getNegotiationStatusDependencyFields = (behaviorConfig) => {
    if (!isConfigEnabled(behaviorConfig?.negotiationStatus)) {
        return [];
    }

    const statusField = behaviorConfig.negotiationStatus.field || NEGOTIATION_STATUS_FIELD;
    const approvalDateField = behaviorConfig.negotiationStatus.approvalDateField || APPROVAL_DATE_FIELD;
    const topTierField = behaviorConfig.negotiationStatus.topTierField || 'topTierRequired';
    const tierApprovalDateFields = Object.values(
        behaviorConfig.negotiationStatus.autoUpdate?.tierApprovalDateFields || TIER_APPROVAL_DATE_FIELDS,
    );

    return [statusField, approvalDateField, topTierField, ...tierApprovalDateFields];
};

/**
 * Returns true when a required field value is present for manual status validation.
 */
const hasManualValidationFieldValue = (value) => {
    if (value === null || value === undefined || value === '') {
        return false;
    }

    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) {
            return false;
        }

        if (/^\d{4}-\d{2}-\d{2}/.test(trimmed) || /^\d{2}\/\d{2}\/\d{4}/.test(trimmed)) {
            return moment.parseZone(trimmed).isValid();
        }

        return true;
    }

    return hasApprovalDate(value);
};

/**
 * Normalizes status values for case-insensitive comparison.
 */
const normalizeStatusValue = (value) => (
    value == null ? '' : String(value).trim().toLowerCase()
);

/**
 * Returns an error message when manually setting negotiationStatus violates manualValidation rules.
 * Example: cannot set "Negotiation Completed" when approvalDate is empty.
 */
export const getManualNegotiationStatusValidationError = (
    fieldKey,
    nextStatusValue,
    row = {},
    behaviorConfig,
) => {
    if (!isConfigEnabled(behaviorConfig?.negotiationStatus)) {
        return null;
    }

    const statusField = behaviorConfig.negotiationStatus.field || NEGOTIATION_STATUS_FIELD;
    if (fieldKey !== statusField) {
        return null;
    }

    const manualValidation = behaviorConfig.negotiationStatus.manualValidation;
    if (!isConfigEnabled(manualValidation)) {
        return null;
    }

    const rules = (manualValidation.rules || []).filter((rule) => isConfigEnabled(rule));
    if (!rules.length) {
        return null;
    }

    const normalizedNextStatus = normalizeStatusValue(nextStatusValue);
    if (!normalizedNextStatus) {
        return null;
    }

    const mergedRow = { ...row, [statusField]: nextStatusValue };

    for (let index = 0; index < rules.length; index += 1) {
        const rule = rules[index];
        if (normalizeStatusValue(rule.status) !== normalizedNextStatus) {
            continue;
        }

        const requireOf = Array.isArray(rule.requireOf) ? rule.requireOf : [];
        const requireAnyOf = Array.isArray(rule.requireAnyOf) ? rule.requireAnyOf : [];

        if (requireOf.length > 0) {
            const hasAllRequired = requireOf.every((requiredField) => (
                hasManualValidationFieldValue(mergedRow[requiredField])
            ));

            if (!hasAllRequired) {
                return rule.message
                    || `Please complete required fields before setting status to ${rule.status}.`;
            }
        }

        if (requireAnyOf.length > 0) {
            const hasAnyRequired = requireAnyOf.some((requiredField) => (
                hasManualValidationFieldValue(mergedRow[requiredField])
            ));

            if (!hasAnyRequired) {
                return rule.message
                    || `Please complete required fields before setting status to ${rule.status}.`;
            }
        }
    }

    return null;
};

/**
 * Returns negotiationStatus payload when a trigger field is saved or cleared.
 */
export const getNegotiationStatusAutoUpdatePayload = (
    fieldKey,
    value,
    row = {},
    behaviorConfig,
) => {
    if (
        !isConfigEnabled(behaviorConfig)
        || !isConfigEnabled(behaviorConfig.negotiationStatus)
        || !isConfigEnabled(behaviorConfig.negotiationStatus.autoUpdate)
    ) {
        return {};
    }

    if (!row || shouldSkipNegotiationStatusAutoUpdate(row, fieldKey, behaviorConfig)) {
        return {};
    }

    const statusField = behaviorConfig.negotiationStatus.field || NEGOTIATION_STATUS_FIELD;
    const topTierField = behaviorConfig.negotiationStatus.topTierField || 'topTierRequired';
    const effectiveTopTier = fieldKey === topTierField ? value : row[topTierField];

    if (!isNegotiationStatusTriggerField(fieldKey, { ...row, [topTierField]: effectiveTopTier }, behaviorConfig)) {
        return {};
    }

    const mergedRow = { ...row, [fieldKey]: value };
    const derivedStatus = deriveNegotiationStatus(mergedRow, behaviorConfig);
    const currentStatus = row[statusField];

    if (derivedStatus === currentStatus) {
        return {};
    }

    return { [statusField]: derivedStatus };
};
